import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, serviceAPI, exportAPI } from '../services/api';
import Layout from '../components/Layout';

// Simple SVG Icons (inline to avoid external dependencies)
const Icons = {
  ClipboardList: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
  CurrencyDollar: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Building: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  Search: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Bell: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  Plus: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Download: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
  ChevronRight: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  )
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_patients: 0,
    total_revenue: 0,
    total_services: 0,
    total_hospitals: 0,
    services_today: 0,
    services_last_month: 0,
    revenue_last_month: 0
  });
  const [recentServices, setRecentServices] = useState([]);
  const [anesthesiaTypeData, setAnesthesiaTypeData] = useState([]);
  const [activeHospitals, setActiveHospitals] = useState([]);
  const [userName, setUserName] = useState('Doctor');
  const [loading, setLoading] = useState(true);
  const [previousMonthStats, setPreviousMonthStats] = useState({
    services: 0,
    revenue: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';
      const token = localStorage.getItem('token');
      
      // Fetch user profile for name
      try {
        const userResp = await fetch(`${API_URL}/users/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (userResp.ok) {
          const userData = await userResp.json();
          setUserName(userData.full_name || userData.email?.split('@')[0] || 'Doctor');
        }
      } catch (error) {
        console.log('Could not fetch user profile');
      }
      
      // Fetch stats and services
      const [statsData, servicesData] = await Promise.all([
        dashboardAPI.getStats(),
        serviceAPI.getAll()
      ]);
      
      // Calculate services added today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const servicesToday = servicesData.filter(service => {
        const serviceDate = new Date(service.created_at || service.service_date);
        return serviceDate >= today;
      }).length;
      
      // Calculate last month's data for percentage comparison
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      
      const lastMonthServices = servicesData.filter(service => {
        const serviceDate = new Date(service.service_date);
        return serviceDate >= lastMonthStart && serviceDate < currentMonthStart;
      });
      
      const currentMonthServices = servicesData.filter(service => {
        const serviceDate = new Date(service.service_date);
        return serviceDate >= currentMonthStart;
      });
      
      const lastMonthRevenue = lastMonthServices.reduce((sum, s) => sum + parseFloat(s.amount_charged || 0), 0);
      const currentMonthRevenue = currentMonthServices.reduce((sum, s) => sum + parseFloat(s.amount_charged || 0), 0);
      
      // Calculate percentage changes
      const servicesChange = lastMonthServices.length > 0 
        ? (((currentMonthServices.length - lastMonthServices.length) / lastMonthServices.length) * 100).toFixed(1)
        : currentMonthServices.length > 0 ? '100.0' : '0.0';
      
      const revenueChange = lastMonthRevenue > 0
        ? (((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100).toFixed(1)
        : currentMonthRevenue > 0 ? '100.0' : '0.0';
      
      setPreviousMonthStats({
        servicesChange: parseFloat(servicesChange) >= 0 ? `+${Math.abs(parseFloat(servicesChange))}%` : `${servicesChange}%`,
        revenueChange: parseFloat(revenueChange) >= 0 ? `+${Math.abs(parseFloat(revenueChange))}%` : `${revenueChange}%`,
        servicesPositive: parseFloat(servicesChange) >= 0,
        revenuePositive: parseFloat(revenueChange) >= 0
      });
      
      setStats({
        ...statsData,
        services_today: servicesToday,
        services_last_month: lastMonthServices.length,
        revenue_last_month: lastMonthRevenue
      });
      setRecentServices(servicesData.slice(-5).reverse());

      // Calculate active hospitals from services
      const hospitalMap = {};
      
      servicesData.forEach(service => {
        const serviceDate = new Date(service.service_date);
        const hospitalName = service.hospital_name || 'Unknown';
        
        if (!hospitalMap[hospitalName]) {
          hospitalMap[hospitalName] = {
            name: hospitalName,
            todayCount: 0,
            totalCount: 0
          };
        }
        
        hospitalMap[hospitalName].totalCount++;
        
        // Check if service is today
        if (serviceDate.toDateString() === today.toDateString()) {
          hospitalMap[hospitalName].todayCount++;
        }
      });

      // Convert to array and sort by today's count, then total count
      const hospitalsArray = Object.values(hospitalMap)
        .sort((a, b) => {
          if (b.todayCount !== a.todayCount) {
            return b.todayCount - a.todayCount;
          }
          return b.totalCount - a.totalCount;
        })
        .slice(0, 3); // Top 3 hospitals

      setActiveHospitals(hospitalsArray);

      // Calculate anesthesia types usage for current month
      const nowForAnesthesia = new Date();
      const currentMonthStartForAnesthesia = new Date(nowForAnesthesia.getFullYear(), nowForAnesthesia.getMonth(), 1);
      
      const currentMonthServicesForAnesthesia = servicesData.filter(service => {
        const serviceDate = new Date(service.service_date);
        return serviceDate >= currentMonthStartForAnesthesia;
      });
      
      const anesthesiaTypeMap = {};
      currentMonthServicesForAnesthesia.forEach(service => {
        const type = service.anesthesia_type || 'Unknown';
        if (!anesthesiaTypeMap[type]) {
          anesthesiaTypeMap[type] = 0;
        }
        anesthesiaTypeMap[type]++;
      });
      
      const totalCurrentMonth = currentMonthServicesForAnesthesia.length;
      const anesthesiaTypes = Object.entries(anesthesiaTypeMap)
        .map(([type, count]) => ({
          type,
          count,
          percentage: totalCurrentMonth > 0 ? ((count / totalCurrentMonth) * 100).toFixed(0) : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5); // Top 5 types
      
      setAnesthesiaTypeData(anesthesiaTypes);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateWeeklyFromServices = (services) => {
    // Not needed anymore - removed weekly activity
  };

  const handleExportAll = async () => {
    try {
      await exportAPI.exportToExcel();
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: 'TOTAL SERVICES',
      value: stats.total_services,
      change: previousMonthStats.servicesChange || '+0.0%',
      subtitle: 'vs last month',
      icon: Icons.ClipboardList,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      positive: previousMonthStats.servicesPositive !== false
    },
    {
      title: 'SERVICES ADDED TODAY',
      value: stats.services_today,
      subtitle: `${stats.services_today} new ${stats.services_today === 1 ? 'service' : 'services'}`,
      icon: Icons.Plus,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      badge: 'Today'
    },
    {
      title: 'MONTHLY EARNINGS',
      value: `$${stats.total_revenue.toLocaleString()}`,
      change: previousMonthStats.revenueChange || '+0.0%',
      subtitle: 'vs last month',
      icon: Icons.CurrencyDollar,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      positive: previousMonthStats.revenuePositive !== false
    }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Welcome, Dr. {userName}</h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Here is what is happening with your services today.</p>
              </div>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors self-start sm:self-auto">
                <Icons.Bell />
              </button>
            </div>
            
            {/* Search Bar - Full width on mobile */}
            <div className="relative w-full">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Icons.Search />
              </div>
              <input
                type="text"
                placeholder="Search patients, hospitals..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          
          {/* Action Buttons - Stack on mobile */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/services/new" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium text-sm"
            >
              <Icons.Plus />
              <span>Log New Service</span>
            </Link>
            <button
              onClick={handleExportAll}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium text-sm"
            >
              <Icons.Download />
              <span>Export Data</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className={`${stat.iconBg} rounded-lg p-2 sm:p-3`}>
                  <div className={stat.iconColor}>
                    <stat.icon />
                  </div>
                </div>
                {stat.change && (
                  <span className={`text-sm font-semibold ${stat.positive ? 'text-green-600' : 'text-red-600'}`}>
                    {stat.change}
                  </span>
                )}
                {stat.badge && (
                  <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded">
                    {stat.badge}
                  </span>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  {stat.title}
                </p>
                <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                <p className="text-xs text-gray-400 italic">{stat.subtitle}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Anesthesia Types Usage - This Month */}
          <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Anesthesia Types Usage</h2>
                <p className="text-sm text-gray-500">Distribution for current month</p>
              </div>
            </div>
            <div className="space-y-4">
              {anesthesiaTypeData.length > 0 ? (
                anesthesiaTypeData.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{item.type}</span>
                      <span className="text-sm font-bold text-blue-600">{item.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-blue-600 to-blue-400 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500">{item.count} procedures</p>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center h-48 text-gray-400">
                  <p>No anesthesia data for this month</p>
                </div>
              )}
            </div>
          </div>

          {/* Active Hospitals */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Hospitals</h2>
            <div className="space-y-3">
              {activeHospitals.length > 0 ? (
                activeHospitals.map((hospital, index) => {
                  const colors = ['blue', 'green', 'cyan'][index] || 'blue';
                  return (
                    <Link 
                      key={index}
                      to="/hospital-earnings" 
                      className={`flex items-center justify-between p-3 bg-${colors}-50 rounded-lg hover:bg-${colors}-100 transition-colors cursor-pointer`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`bg-${colors}-100 rounded-lg p-2`}>
                          <div className={`text-${colors}-600`}>
                            <Icons.Building />
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{hospital.name}</p>
                          <p className="text-xs text-gray-500">
                            {hospital.todayCount > 0 
                              ? `${hospital.todayCount} Proc${hospital.todayCount > 1 ? 's' : ''} Today`
                              : `${hospital.totalCount} Total Services`
                            }
                          </p>
                        </div>
                      </div>
                      <Icons.ChevronRight />
                    </Link>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">No hospital data available</p>
                  <Link to="/services/new" className="text-blue-600 hover:text-blue-700 font-medium text-xs mt-2 inline-block">
                    Add your first service →
                  </Link>
                </div>
              )}

              <Link to="/hospital-earnings" className="block w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-2 hover:bg-blue-50 rounded-lg transition-colors">
                View All Hospitals
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Services */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Recent Services</h2>
            <Link to="/services" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              Full History
              <Icons.ChevronRight />
            </Link>
          </div>
          
          {recentServices.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 text-gray-300 mx-auto mb-4">
                <Icons.ClipboardList />
              </div>
              <p className="text-gray-500 mb-2">No services recorded yet.</p>
              <Link to="/services/new" className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                Add your first service →
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient & ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Procedure
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hospital
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fee
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recentServices.map((service) => (
                    <tr key={service.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {service.patient_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            #{service.patient_number}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {service.anesthesia_type}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {service.hospital_name}
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Paid
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                        ${service.amount_charged.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;

